// WP-C1.1 (INT-1/2/4, S-39..46 "Calendars"): the Settings→Calendars page — the same
// connect-panel used by onboarding step ⑥, plus the poller cadence note (INT-2).
// WP-C2.1/C2.2 (INT-3): Track C2's OAuth entry point (Google/Microsoft, near-real-time,
// revocable) added alongside — reflects the callback redirect's one-time
// `?connected=`/`?connectError=` query flags as a toast, the same reuse of Toast.tsx a
// one-time post-redirect status elsewhere in this app would use.
import { useEffect, useState } from 'react';
import type { CalendarOAuthProvider } from '@pfos/shared';
import { CalendarConnectPanel } from '../../components/CalendarConnectPanel';
import { Toast } from '../../components/Toast';
import { useStartCalendarOAuth } from '../../lib/calendars';

// Partial (not Record<CalendarOAuthProvider, string>) so an unrecognized `connected` query
// value — which reaches this lookup as a plain string, not a type-narrowed provider — falls
// through to the `?? connected` fallback below rather than TS treating the index as total.
const PROVIDER_LABEL: Partial<Record<string, string>> = {
  google: 'Google',
  microsoft: 'Microsoft',
};

function OAuthConnectButtons(): React.JSX.Element {
  const start = useStartCalendarOAuth();
  const [error, setError] = useState<string | null>(null);

  const connect = (provider: CalendarOAuthProvider): void => {
    setError(null);
    start.mutate(provider, {
      onSuccess: (data) => {
        window.location.assign(data.authorizationUrl);
      },
      onError: () => {
        setError(
          `Couldn't start ${PROVIDER_LABEL[provider] ?? provider} sign-in — check it's configured in Settings/env.`,
        );
      },
    });
  };

  return (
    <div className="mt-4 border-t border-border pt-4">
      <p className="mb-2 text-xs text-muted">
        Or connect via OAuth (Track C2) for near-real-time sync — tokens are stored encrypted and
        can be revoked here at any time (INT-3/SB-4).
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="rounded-[var(--radius-control)] border border-border px-3 py-1.5 text-sm text-ink hover:border-primary"
          disabled={start.isPending}
          onClick={() => {
            connect('google');
          }}
        >
          Connect Google Calendar
        </button>
        <button
          type="button"
          className="rounded-[var(--radius-control)] border border-border px-3 py-1.5 text-sm text-ink hover:border-primary"
          disabled={start.isPending}
          onClick={() => {
            connect('microsoft');
          }}
        >
          Connect Microsoft Calendar
        </button>
      </div>
      {error !== null && <p className="mt-2 text-xs text-danger">{error}</p>}
    </div>
  );
}

/** Reads the callback redirect's one-time `?connected=google|microsoft` / `?connectError=1`
 * flags, then strips them from the URL (history.replaceState) so a page refresh never
 * re-shows the same toast. */
function useOAuthRedirectBanner(): { message: string; isError: boolean } | null {
  const [banner, setBanner] = useState<{ message: string; isError: boolean } | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    const connectError = params.get('connectError');
    if (connected === null && connectError === null) return;

    if (connected !== null) {
      const label = PROVIDER_LABEL[connected] ?? connected;
      setBanner({ message: `${label} Calendar connected.`, isError: false });
    } else {
      setBanner({ message: "Couldn't connect that calendar — please try again.", isError: true });
    }

    params.delete('connected');
    params.delete('connectError');
    const query = params.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${query !== '' ? `?${query}` : ''}`,
    );
  }, []);

  return banner;
}

export function SettingsCalendars(): React.JSX.Element {
  const banner = useOAuthRedirectBanner();
  const [dismissed, setDismissed] = useState(false);

  return (
    <div className="space-y-6">
      <section className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <h2 className="mb-1 text-sm font-medium text-ink">Connected calendars</h2>
        <p className="mb-3 text-xs text-muted">
          Read-only (Track C1) — events refresh automatically every 10-15 minutes, or tap refresh
          for the latest (INT-2: provider feeds can lag a few hours upstream).
        </p>
        <CalendarConnectPanel />
        <OAuthConnectButtons />
      </section>
      {banner !== null && !dismissed && (
        <Toast
          message={banner.message}
          onDismiss={() => {
            setDismissed(true);
          }}
        />
      )}
    </div>
  );
}
